import { useCallback, useEffect, useRef } from "react";
import { backgroundSignal } from "../lib/requestActivity";
import { watchBrandingChanges } from "../lib/brandingChanges";

export const BRANDING_REFRESH_MS = 15000;

/** Refresh branding in place; never reset the page's loading state or children. */
export default function useLiveBranding(scope, load, accept, { initial = false } = {}) {
  const current = useRef({ scope, accept });
  current.current = { scope, accept };
  const runner = useRef(null);
  useEffect(() => {
    if (!scope) return undefined;
    let active = true, request = null;
    const run = async (replace = false) => {
      if (!active || current.current.scope !== scope || (!replace && document.visibilityState === "hidden")) return;
      if (request && !replace) return;
      request?.abort();
      const controller = new AbortController(); request = controller;
      try {
        const data = await load(backgroundSignal(controller.signal));
        if (active && request === controller && !controller.signal.aborted && current.current.scope === scope) current.current.accept(data);
      } catch { /* Retain the last resolved brand; the next check can recover. */ }
      finally { if (request === controller) request = null; }
    };
    runner.current = { scope, run };
    const invalidate = () => { if (document.visibilityState !== "hidden") void run(true); };
    const visible = () => { if (document.visibilityState === "hidden") { request?.abort(); request = null; } else invalidate(); };
    const stop = watchBrandingChanges(scope, invalidate);
    const timer = setInterval(() => { void run(); }, BRANDING_REFRESH_MS);
    window.addEventListener("focus", invalidate);
    window.addEventListener("pageshow", invalidate);
    document.addEventListener("visibilitychange", visible);
    if (initial) void run();
    return () => {
      active = false; request?.abort(); clearInterval(timer); stop();
      window.removeEventListener("focus", invalidate); window.removeEventListener("pageshow", invalidate);
      document.removeEventListener("visibilitychange", visible);
      if (runner.current?.scope === scope) runner.current = null;
    };
  }, [scope, load, initial]);
  return useCallback(() => runner.current?.scope === current.current.scope ? runner.current.run(true) : Promise.resolve(), []);
}
